import React, { useState, useEffect } from 'react';
import { useGameAudio } from './GameAudioProvider';

interface GameSuccessScreenProps {
  totalQuestions: number;
  totalWins: number;
  totalTime: number;
  onPlayAgain: () => void;
}

const GameSuccessScreen: React.FC<GameSuccessScreenProps> = ({
  totalQuestions,
  totalWins,
  totalTime,
  onPlayAgain
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const winPercentage = Math.round((totalWins / totalQuestions) * 100);
  const { kinzySpeak } = useGameAudio();
  const minutes = Math.floor(totalTime / 60);
  const seconds = totalTime % 60;

  useEffect(() => {
    setIsLoaded(true);
    // Voice narration when success screen loads
    const message = `Kingdom Conquered! ${getPerformanceMessage()} You won ${totalWins} out of ${totalQuestions} battles with a ${winPercentage}% victory rate!`;
    kinzySpeak(message);
    
  }, [totalWins, totalQuestions, winPercentage, kinzySpeak]);

  const getPerformanceMessage = () => {
    if (winPercentage >= 90) return "🌟 Legendary Champion! 🌟";
    if (winPercentage >= 70) return "🏆 Mighty Warrior! 🏆";
    if (winPercentage >= 50) return "⚔️ Brave Knight! ⚔️";
    return "🛡️ Noble Defender! 🛡️";
  };

  const getPerformanceColor = () => {
    if (winPercentage >= 90) return "from-yellow-400 to-orange-500";
    if (winPercentage >= 70) return "from-green-400 to-blue-500";
    if (winPercentage >= 50) return "from-blue-400 to-purple-500";
    return "from-purple-400 to-pink-500";
  };

  return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-10">
          {Array.from({ length: 25 }, (_, i) => (
            <div
              key={i}
              className="absolute bg-gradient-to-r from-yellow-300 to-pink-300 rounded-full animate-bounce shadow-lg"
              style={{
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + '%',
                width: Math.random() * 25 + 10 + 'px',
                height: Math.random() * 25 + 10 + 'px',
                animationDelay: Math.random() * 3 + 's',
                animationDuration: Math.random() * 3 + 2 + 's'
              }}
            />  
          ))}
        </div>

        <div className={`flex flex-col items-center justify-center min-h-screen px-4 py-8 transform transition-all duration-1000 ${
          isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          
          {/* Success header */}
          <div className="text-center mb-8">
            <div className="text-4xl mb-4 animate-bounce">
              🏰👑
            </div>
            <div className="mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                Kingdom Conquered!
              </h1>
            </div>
            <div>
              <p className="text-lg text-white/90 font-medium drop-shadow-md">
                {getPerformanceMessage()}
              </p>
            </div>
          </div>

          {/* Stats display */}
          <div className="bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-lg rounded-xl p-4 shadow-lg border border-white/20 mb-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xl font-bold text-white drop-shadow-md mb-1">{totalWins}/{totalQuestions}</div>
                <div className="text-white/80 text-sm font-medium">Battles Won</div>
              </div>
              <div>
                <div className="text-xl font-bold text-white drop-shadow-md mb-1">{winPercentage}%</div>
                <div className="text-white/80 text-sm font-medium">Victory Rate</div>
              </div>
              <div>
                <div className="text-xl font-bold text-white drop-shadow-md mb-1">{minutes}:{seconds.toString().padStart(2, '0')}</div>
                <div className="text-white/80 text-sm font-medium">Total Time</div>
              </div>
            </div>
          </div>

          {/* Play again button */}
          <button
            onClick={onPlayAgain}
            className={`py-3 px-6 text-base font-semibold text-white bg-gradient-to-r ${getPerformanceColor()} rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 active:scale-95`}
          >
            🏰 Play Again
          </button>

        </div>
      </div>
  );
};

export { GameSuccessScreen };